import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import { useState, useRef } from 'react';
import throttle from 'lodash/throttle';
import ddt from '@wix/da-ddt';
import { useLayoutEffectWithoutSSRComplaining } from '../useLayoutEffectWithoutSSRComplaining';
const logger = ddt.createLogger('folding');
const THROTTLE_TIMEOUT = 200;
const FALSE_FN = ()=>false;
// Measures children of an element and calculates which children should be hidden
// to fit into a certain number of rows. It identifies children based on text content.
// for example usage see src/Tags/Tags.tsx
export function useChildrenFolding(items, { maxLines =1 , itemSpacing =0 , endPadding =0 , maxItemWidth , getItemKey , excludeChild =FALSE_FN  }) {
    const containerRef = useRef(null);
    const [overflowItems, setOverflowItems] = useState(0);
    const [childWidths, setChildWidths] = useState({});
    // reset width tracking entirely if items have changed
    useLayoutEffectWithoutSSRComplaining(()=>{
        logger.log('items have changed, reseting width tracking states...');
        setOverflowItems(0);
        setChildWidths({});
    }, [
        items
    ]);
    // run through items adding up rows based on container and item widths,
    // marking any past maxLines as hidden
    useLayoutEffectWithoutSSRComplaining(()=>{
        const calculateOverflow = throttle(()=>{
            const container = containerRef.current;
            if (!container) {
                return;
            }
            const newTagWidths = _object_spread({}, childWidths);
            let updateTagWidths = false;
            const containerWidth = container.getBoundingClientRect().width;
            const lastRowWidth = containerWidth - endPadding;
            let rows = 1;
            let newOverflowItems = 0;
            let currentRowWidth = 0;
            Array.prototype.forEach.call(container.children, (element)=>{
                const tagText = element.textContent || '';
                if (excludeChild(element)) {
                    logger.log(`item: '${tagText}' excluded`);
                    return;
                }
                // we only measure if we haven't done so before - so collapsed
                // long items keep the right size
                if (!newTagWidths.hasOwnProperty(tagText)) {
                    newTagWidths[tagText] = Math.ceil(element.getBoundingClientRect().width);
                    updateTagWidths = true;
                }
                const tagWidth = (maxItemWidth ? Math.min(newTagWidths[tagText], maxItemWidth) : newTagWidths[tagText]) + itemSpacing;
                logger.log(`item '${tagText}' width: ${tagWidth}`);
                if (rows < maxLines) {
                    if (currentRowWidth + tagWidth < containerWidth) {
                        currentRowWidth += tagWidth;
                        logger.log(`Row ${rows}: ${tagText} (${tagWidth}/${currentRowWidth})`);
                    } else {
                        logger.log(`Row ${rows} final width: ${currentRowWidth}`);
                        rows++;
                        currentRowWidth = tagWidth;
                        logger.log(`Row ${rows}: ${tagText} (${tagWidth}/${currentRowWidth})`);
                    }
                } else if (rows === maxLines && currentRowWidth + tagWidth < lastRowWidth) {
                    currentRowWidth += tagWidth;
                    logger.log(`Row ${rows}: ${tagText} (${tagWidth}/${currentRowWidth})`);
                } else {
                    if (rows === maxLines) {
                        logger.log(`Row ${rows} final width: ${currentRowWidth + endPadding}`);
                    }
                    rows = maxLines + 1;
                    newOverflowItems++;
                    logger.log(`Overflowed: ${tagText} (${tagWidth})`);
                }
            });
            // Only set overflow when it has changed, stops death loop
            if (overflowItems !== newOverflowItems) {
                logger.log(`Set overflow was: ${overflowItems} now: ${newOverflowItems}`);
                setOverflowItems(newOverflowItems);
            }
            if (updateTagWidths) {
                logger.log(`Set child width`);
                setChildWidths(newTagWidths);
            }
        }, THROTTLE_TIMEOUT, {
            leading: true
        });
        if (overflowItems === 0) {
            calculateOverflow();
        }
        window.addEventListener('resize', calculateOverflow);
        return ()=>window.removeEventListener('resize', calculateOverflow);
    }, [
        items,
        containerRef,
        childWidths,
        overflowItems,
        maxLines,
        itemSpacing,
        endPadding,
        maxItemWidth,
        excludeChild
    ]);
    const sizedItems = items.map((item, index)=>{
        const key = getItemKey(item);
        const width = childWidths[key];
        return {
            item,
            width,
            isOverflow: index >= items.length - overflowItems,
            isOversized: maxItemWidth ? (width || 0) > maxItemWidth : false
        };
    });
    return {
        sizedItems,
        hasHidden: overflowItems > 0,
        containerRef
    };
}
